import { useShifts, useUsers } from '@/api/data';
/** Arbeidstidsoversikt, AML §10-7: the log per employee for a period, printable. Read only. */
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { breakPolicy, oslo, today, worked, workedResult, workedSum } from '@/rules';
import { useTenant } from '@/shell/tenant';
import { Button, Empty, Panel } from '@/ui';
import { DN, fmtD, fmtDs, fullName, num } from '@/ui/format';
import { dow, isHolyDay, mondayOf } from '@timefly/core';
import { useState } from 'react';
import { overtimeWindows } from './record';

const firstOf = (ym: string) => `${ym}-01`;
const lastOf = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return `${ym}-${String(new Date(Date.UTC(y ?? 2026, m ?? 1, 0)).getUTCDate()).padStart(2, '0')}`;
};

export default function Register() {
  const can = useCan();
  const { tenant } = useTenant();
  const policy = breakPolicy(tenant);
  const [ym, setYm] = useState(today().slice(0, 7));
  const [who, setWho] = useState<'alle' | number>('alle');
  const from = firstOf(ym);
  const to = lastOf(ym);
  const usersQ = useUsers();
  // from the Monday of the first week, so the § 10-6 week that began in the month before counts
  // its earlier days (2026-09-23); only the month's own rows are shown
  const shiftsQ = useShifts(mondayOf(from), to);
  const fetched = shiftsQ.data?.shifts ?? [];
  const inMonth = (s: (typeof fetched)[number]) => {
    const d = oslo(s.start_time)?.date ?? '';
    return d >= from && d <= to;
  };
  const shifts = fetched.filter(inMonth);
  // AML §10-7: the record is of everyone who worked in the period, including people who have left
  // since (law register REC-10-7-1, 2026-09-22); until then it listed active staff only
  const workedIds = new Set(shifts.map((s) => s.user_id));
  const users = (usersQ.data ?? [])
    .filter((u) => u.active_status || workedIds.has(u.id))
    .sort((a, b) => fullName(a).localeCompare(fullName(b), 'nb'));
  const list = who === 'alle' ? users : users.filter((u) => u.id === who);
  return (
    <>
      <div className="ph">
        <div>
          <h1>{T('Arbeidstidsoversikt')}</h1>
          <div className="sub">
            AML §10-7, {fmtD(from)} {T('til {day}', { day: fmtD(to) })}, {tenant?.name ?? ''}
          </div>
        </div>
        <input
          className="inp"
          type="month"
          aria-label={T('Periode')}
          value={ym}
          onChange={(e) => e.target.value && setYm(e.target.value)}
        />
        <select
          className="inp"
          aria-label={T('Ansatt')}
          value={who}
          onChange={(e) => setWho(e.target.value === 'alle' ? 'alle' : Number(e.target.value))}
        >
          <option value="alle">{T('Alle ansatte')}</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {fullName(u)}
            </option>
          ))}
        </select>
        <span className="sp" />
        <Button onClick={() => window.print()}>{T('Skriv ut')}</Button>
      </div>
      <div className="al info">
        <span>§</span>
        <span>
          {T(
            'Fortløpende oversikt over arbeidstid: dato, start og slutt, pauser, overtid, natt- og søndagsarbeid. Arbeidsgiver er ansvarlig også når den ansatte registrerer selv. Oppbevares i minst 3½ år.',
          )}
        </span>
      </div>
      {list.map((u) => {
        const all = fetched
          .filter((s) => s.user_id === u.id)
          .sort((a, b) => a.start_time.localeCompare(b.start_time));
        const ss = all.filter(inMonth);
        // the same § 10-6 overtime the file records, so the print and the file say one thing
        const overtime = overtimeWindows(all, policy, u);
        return (
          <Panel
            key={u.id}
            title={fullName(u)}
            // employee number and agreed hours are the administrator's fields: absent for anyone
            // else, so the subtitle says what it knows rather than a default (TF-SIMPLE-ROLES)
            sub={[
              u.employee_id ? T('ansattnr {id}', { id: u.employee_id }) : null,
              u.max_hours_per_week != null
                ? T('avtalt {h} t/uke', { h: num(u.max_hours_per_week) })
                : can('ansatte.endre')
                  ? T('avtalt 37,5* t/uke')
                  : null,
            ]
              .filter(Boolean)
              .join(', ')}
            actions={
              <span className="sub">
                {((h) => (h == null ? '–' : `${num(h)} t`))(workedSum(ss, policy))}{' '}
                {T('etter pause i perioden, hele vakter')}
              </span>
            }
            flush
          >
            {ss.length ? (
              <div className="tw">
                <table className="t">
                  <thead>
                    <tr>
                      <th>{T('Dato')}</th>
                      <th>{T('Planlagt')}</th>
                      <th>{T('Inn')}</th>
                      <th>{T('Ut')}</th>
                      <th>{T('Pause')}</th>
                      <th className="n">{T('Timer etter pause')}</th>
                      <th>{T('Overtid, natt, søndag')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ss.map((s) => {
                      const d = oslo(s.start_time)?.date ?? '';
                      const h = worked(s, policy) ?? 0; // null (no punches or policy not loaded) shows as no value below
                      const pin = oslo(s.punch_in_time)?.time;
                      const pout = oslo(s.punch_out_time)?.time;
                      // the agreed day is the agreed week over five; with the week withheld there
                      // is no agreed day to be over, so the mark is left off rather than guessed
                      const dayNorm = u.max_hours_per_week == null ? null : u.max_hours_per_week / 5;
                      const ot = overtime.get(s.id);
                      const marks = [
                        ot ? T('overtid {from}–{to}', { from: ot.from, to: ot.to }) : '',
                        dayNorm != null && h > dayNorm + 0.25 ? T('over {h} t', { h: num(dayNorm) }) : '',
                        // a registration ending on a clock time before it started ran through midnight
                        pin && ((pout ?? '') > '21:00' || pin < '06:00' || (pout != null && pout < pin))
                          ? T('natt')
                          : '',
                        // AML §10-10 counts a public holiday as a søndag, as the CSV does
                        isHolyDay(d) ? T('søn-/helgedag') : '',
                      ]
                        .filter(Boolean)
                        .join(', ');
                      // the break the hours were calculated with, from the same rule (workedResult): with
                      // an automatic break the registered one only shows as a note. The column showed the
                      // registered break while the hours used the automatic one until 2026-09-21, and this
                      // is the §10-7 record, so the two must never disagree.
                      const r = workedResult(s, policy);
                      const took = r.status === 'ok' ? Math.round(r.deductedMinutes) : null;
                      const registered =
                        s.lunch_in_time && s.lunch_out_time
                          ? `${oslo(s.lunch_in_time)?.time}–${oslo(s.lunch_out_time)?.time}`
                          : null;
                      // the shift's own mode (plan 0032); under punched_min the automatic minimum shows as such
                      const mode = s.break_mode ?? policy?.mode ?? (policy?.auto ? 'auto' : 'punched');
                      const recorded =
                        s.lunch_in_time && s.lunch_out_time
                          ? (Date.parse(s.lunch_out_time) - Date.parse(s.lunch_in_time)) / 60000
                          : 0;
                      const automatic =
                        mode === 'auto' || (mode === 'punched_min' && took != null && took > recorded);
                      const pause =
                        took == null
                          ? '–'
                          : automatic
                            ? `${took} min, ${mode === 'auto' ? T('automatisk') : T('automatisk minimum')}${registered ? ` (${T('registrert')} ${registered})` : ''}`
                            : `${took} min${registered ? ` ${registered}` : ''}`;
                      return (
                        <tr key={s.id}>
                          <td>
                            {DN[dow(d)]} {fmtDs(d)}
                          </td>
                          <td className="mono">
                            {oslo(s.start_time)?.time}–{oslo(s.end_time)?.time}
                          </td>
                          <td className="mono">{pin ?? '–'}</td>
                          <td className="mono">
                            {pout ?? (pin ? <span style={{ color: 'var(--dgr-i)' }}>{T('åpen')}</span> : '–')}
                          </td>
                          <td>{pause}</td>
                          <td className="n">
                            {pin ? (
                              num(h)
                            ) : (
                              <span style={{ color: 'var(--ink3)', fontWeight: 500 }}>plan {num(h)}</span>
                            )}
                          </td>
                          <td>{marks || '–'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <Empty>{T('Ingen registreringer i perioden.')}</Empty>
            )}
          </Panel>
        );
      })}
    </>
  );
}
